/**
 * Hero Particles — Awwwards-grade golden dust system
 * Simplex noise flow field, depth-of-field, glow shaders, constellation
 * lines, breathing cloud, mouse repulsion, scroll-burst dissolve, entry burst.
 *
 * Expects THREE globally (loaded via CDN).
 * Reads config from data attributes on the canvas element.
 */

/* ---- Compact 3D Simplex Noise (self-contained, no deps) ---- */
const _g3=[[1,1,0],[-1,1,0],[1,-1,0],[-1,-1,0],[1,0,1],[-1,0,1],[1,0,-1],[-1,0,-1],[0,1,1],[0,-1,1],[0,1,-1],[0,-1,-1]];
const _p=[151,160,137,91,90,15,131,13,201,95,96,53,194,233,7,225,140,36,103,30,69,142,8,99,37,240,21,10,23,190,6,148,247,120,234,75,0,26,197,62,94,252,219,203,117,35,11,32,57,177,33,88,237,149,56,87,174,20,125,136,171,168,68,175,74,165,71,134,139,48,27,166,77,146,158,231,83,111,229,122,60,211,133,230,220,105,92,41,55,46,245,40,244,102,143,54,65,25,63,161,1,216,80,73,209,76,132,187,208,89,18,169,200,196,135,130,116,188,159,86,164,100,109,198,173,186,3,64,52,217,226,250,124,123,5,202,38,147,118,126,255,82,85,212,207,206,59,227,47,16,58,17,182,189,28,42,223,183,170,213,119,248,152,2,44,154,163,70,221,153,101,155,167,43,172,9,129,22,39,253,19,98,108,110,79,113,224,232,178,185,112,104,218,246,97,228,251,34,242,193,238,210,144,12,191,179,162,241,81,51,145,235,249,14,239,107,49,192,214,31,181,199,106,157,184,84,204,176,115,121,50,45,127,4,150,254,138,236,205,93,222,114,67,29,24,72,243,141,128,195,78,66,215,61,156,180];
const _pm=new Uint8Array(512),_pm12=new Uint8Array(512);
for(let i=0;i<512;i++){_pm[i]=_p[i&255];_pm12[i]=_pm[i]%12;}
function noise3(x,y,z){
  const F=1/3,G=1/6,s=(x+y+z)*F,i=Math.floor(x+s),j=Math.floor(y+s),k=Math.floor(z+s),t=(i+j+k)*G;
  const x0=x-(i-t),y0=y-(j-t),z0=z-(k-t);
  let i1,j1,k1,i2,j2,k2;
  if(x0>=y0){if(y0>=z0){i1=1;j1=0;k1=0;i2=1;j2=1;k2=0;}else if(x0>=z0){i1=1;j1=0;k1=0;i2=1;j2=0;k2=1;}else{i1=0;j1=0;k1=1;i2=1;j2=0;k2=1;}}
  else{if(y0<z0){i1=0;j1=0;k1=1;i2=0;j2=1;k2=1;}else if(x0<z0){i1=0;j1=1;k1=0;i2=0;j2=1;k2=1;}else{i1=0;j1=1;k1=0;i2=1;j2=1;k2=0;}}
  const x1=x0-i1+G,y1=y0-j1+G,z1=z0-k1+G,x2=x0-i2+2*G,y2=y0-j2+2*G,z2=z0-k2+2*G,x3=x0-1+.5,y3=y0-1+.5,z3=z0-1+.5;
  const ii=i&255,jj=j&255,kk=k&255;
  let n0=0,n1=0,n2=0,n3=0;
  let u0=.6-x0*x0-y0*y0-z0*z0; if(u0>0){u0*=u0;const g=_g3[_pm12[ii+_pm[jj+_pm[kk]]]];n0=u0*u0*(g[0]*x0+g[1]*y0+g[2]*z0);}
  let u1=.6-x1*x1-y1*y1-z1*z1; if(u1>0){u1*=u1;const g=_g3[_pm12[ii+i1+_pm[jj+j1+_pm[kk+k1]]]];n1=u1*u1*(g[0]*x1+g[1]*y1+g[2]*z1);}
  let u2=.6-x2*x2-y2*y2-z2*z2; if(u2>0){u2*=u2;const g=_g3[_pm12[ii+i2+_pm[jj+j2+_pm[kk+k2]]]];n2=u2*u2*(g[0]*x2+g[1]*y2+g[2]*z2);}
  let u3=.6-x3*x3-y3*y3-z3*z3; if(u3>0){u3*=u3;const g=_g3[_pm12[ii+1+_pm[jj+1+_pm[kk+1]]]];n3=u3*u3*(g[0]*x3+g[1]*y3+g[2]*z3);}
  return 32*(n0+n1+n2+n3);
}

/* Easing */
function easeOutExpo(t) { return t >= 1 ? 1 : 1 - Math.pow(2, -10 * t); }

/* ======================================================================== */

class HeroParticles {
  #canvas; #renderer; #scene; #camera;
  #points; #geo; #mat;
  #lines; #lineGeo; #lineMat;
  #raf; #mouse; #scroll; #clock;
  #dead; #reducedMotion;
  #base; #vel; #colorMix;
  #entry; #entryDur; #count; #mw;
  #cachedRect;
  #onR; #onM; #onS; #onCL; #onCR;

  constructor(canvasId, opts = {}) {
    this.#canvas = document.getElementById(canvasId);
    if (!this.#canvas) return;

    const cores = navigator.hardwareConcurrency || 2;
    const lowEnd = cores < 4;
    this.#count = lowEnd ? Math.min(opts.particleCount || 200, 90) : (opts.particleCount || 200);
    this.color = opts.particleColor || '#f06306';
    this.sens = opts.mouseSensitivity || 0.3;
    this.#mouse = { x: 0, y: 0, tx: 0, ty: 0 };
    this.#mw = new THREE.Vector3();
    this.#scroll = { p: 0 };
    this.#cachedRect = { top: 0, height: 0 };
    this.#dead = false;
    this.#entry = 0;
    this.#entryDur = 2.0;
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#init();
  }

  #init() {
    if (typeof THREE === 'undefined') { console.warn('HeroParticles: THREE.js not loaded.'); return; }
    this.#clock = new THREE.Clock();
    const desktop = window.innerWidth >= 1024;

    this.#renderer = new THREE.WebGLRenderer({ canvas: this.#canvas, alpha: true, antialias: desktop });
    this.#renderer.setSize(window.innerWidth, window.innerHeight);
    this.#renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    this.#scene = new THREE.Scene();
    this.#camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 1000);
    this.#camera.position.z = 50;

    this.#buildParticles();
    this.#buildLines();
    this.#bindEvents();
    this.#cacheRect();

    if (this.#reducedMotion) { this.#entry = 1; this.#renderer.render(this.#scene, this.#camera); }
    else this.#loop();
  }

  #buildParticles() {
    const n = this.#count;
    this.#geo = new THREE.BufferGeometry();
    const pos = new Float32Array(n * 3), sz = new Float32Array(n), op = new Float32Array(n), cm = new Float32Array(n);
    this.#vel = new Float32Array(n * 3);

    for (let i = 0; i < n; i++) {
      const r = 30 + Math.random() * 30, th = Math.random() * Math.PI * 2, ph = Math.acos(2 * Math.random() - 1);
      pos[i*3] = r * Math.sin(ph) * Math.cos(th);
      pos[i*3+1] = r * Math.sin(ph) * Math.sin(th);
      pos[i*3+2] = r * Math.cos(ph) - 20;
      sz[i] = 1 + Math.pow(Math.random(), 3) * 7;           /* power distribution: mostly small */
      op[i] = 0.1 + Math.pow(Math.random(), 0.5) * 0.9;
      cm[i] = Math.random();
    }
    this.#base = new Float32Array(pos);
    this.#colorMix = cm;

    this.#geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    this.#geo.setAttribute('aSize', new THREE.BufferAttribute(sz, 1));
    this.#geo.setAttribute('aOpacity', new THREE.BufferAttribute(op, 1));
    this.#geo.setAttribute('aColorMix', new THREE.BufferAttribute(cm, 1));

    this.#mat = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
      uniforms: {
        uTime: { value: 0 }, uScrollFade: { value: 1 }, uBreath: { value: 0 },
        uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
        uColorA: { value: new THREE.Color('#f06306') },
        uColorB: { value: new THREE.Color('#D4A853') },
        uColorC: { value: new THREE.Color('#6B4C4C') },
      },
      vertexShader: /* glsl */`
        attribute float aSize; attribute float aOpacity; attribute float aColorMix;
        varying float vOp; varying float vCm; varying float vDepth;
        uniform float uTime, uScrollFade, uPixelRatio, uBreath;
        void main(){
          vec3 p = position * (1.0 + uBreath * 0.06);
          vec4 mv = modelViewMatrix * vec4(p, 1.0);
          float zD = abs(mv.z);
          float df = clamp(1.0 - (zD - 30.0) / 60.0, 0.2, 1.0);
          vDepth = df; vOp = aOpacity * uScrollFade * df; vCm = aColorMix;
          gl_PointSize = max(aSize * df * uPixelRatio * (45.0 / max(zD, 1.0)), 1.0);
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */`
        varying float vOp, vCm, vDepth;
        uniform vec3 uColorA, uColorB, uColorC;
        void main(){
          float d = length(gl_PointCoord - vec2(0.5));
          if(d > 0.5) discard;
          float glow = exp(-d * 5.0) * 0.6 + (1.0 - smoothstep(0.0, 0.45, d)) * 0.4;
          glow = pow(glow, 1.5);
          vec3 c = mix(uColorA, uColorB, smoothstep(0.0, 0.5, vCm));
          c = mix(c, uColorC, smoothstep(0.5, 1.0, vCm));
          c += vec3(0.05) * vDepth;
          gl_FragColor = vec4(c, glow * vOp);
        }`,
    });

    this.#points = new THREE.Points(this.#geo, this.#mat);
    this.#scene.add(this.#points);
  }

  #buildLines() {
    const max = this.#count * 3;
    this.#lineGeo = new THREE.BufferGeometry();
    this.#lineGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(max * 6), 3));
    this.#lineGeo.setDrawRange(0, 0);
    this.#lineMat = new THREE.LineBasicMaterial({ color: 0xC8943E, transparent: true, opacity: 0.03, blending: THREE.AdditiveBlending, depthWrite: false });
    this.#lines = new THREE.LineSegments(this.#lineGeo, this.#lineMat);
    this.#scene.add(this.#lines);
  }

  #updateLines(pos, n) {
    const lp = this.#lineGeo.attributes.position.array;
    const thr2 = 144; /* 12^2 world units, ~80px at z=50 */
    let idx = 0;
    const cap = lp.length / 3;
    const step = n > 120 ? 3 : 1;
    for (let i = 0; i < n && idx < cap - 6; i += step) {
      const ax = pos[i*3], ay = pos[i*3+1], az = pos[i*3+2];
      for (let j = i + step; j < n && idx < cap - 6; j += step) {
        const dx = pos[j*3]-ax, dy = pos[j*3+1]-ay, dz = pos[j*3+2]-az;
        if (dx*dx+dy*dy+dz*dz < thr2) {
          lp[idx++]=ax;lp[idx++]=ay;lp[idx++]=az;
          lp[idx++]=pos[j*3];lp[idx++]=pos[j*3+1];lp[idx++]=pos[j*3+2];
        }
      }
    }
    this.#lineGeo.setDrawRange(0, idx / 3);
    this.#lineGeo.attributes.position.needsUpdate = true;
  }

  #loop() {
    if (this.#dead) return;
    this.#raf = requestAnimationFrame(() => this.#loop());

    const dt = Math.min(this.#clock.getDelta(), 0.05);
    const t = this.#clock.getElapsedTime();

    /* Entry burst from center */
    if (this.#entry < 1) this.#entry = Math.min(this.#entry + dt / this.#entryDur, 1);
    const eE = easeOutExpo(this.#entry);

    /* Smooth mouse */
    this.#mouse.x += (this.#mouse.tx - this.#mouse.x) * 0.05;
    this.#mouse.y += (this.#mouse.ty - this.#mouse.y) * 0.05;
    this.#mw.set(this.#mouse.x * 40, -this.#mouse.y * 25, 0);

    /* Breathing (~4s cycle) */
    this.#mat.uniforms.uBreath.value = Math.sin(t * Math.PI * 0.5);
    this.#mat.uniforms.uTime.value = t;

    /* Scroll dissolve */
    const sp = this.#scroll.p;
    const sf = Math.max(1 - sp * 1.6, 0);
    this.#mat.uniforms.uScrollFade.value = sf;
    this.#lineMat.opacity = 0.03 * sf;

    /* Per-particle: noise flow field + mouse repulsion + scroll burst */
    const pos = this.#geo.attributes.position.array;
    const base = this.#base, vel = this.#vel, n = this.#count;
    const ns = 0.02, nt = t * 0.08;
    const mr2 = 225; /* 15^2 world units = ~150px */
    const sb = sp > 0.01 ? Math.pow(sp, 0.6) * 2.5 : 0;

    for (let i = 0; i < n; i++) {
      const i3 = i * 3;
      const tx = base[i3] * eE, ty = base[i3+1] * eE, tz = base[i3+2] * eE;

      /* 3D simplex noise flow field */
      const fx = noise3(tx*ns+nt, ty*ns, tz*ns) * 1.8;
      const fy = noise3(tx*ns, ty*ns+nt, tz*ns+100) * 1.8;
      const fz = noise3(tx*ns+200, ty*ns, tz*ns+nt) * 1.0;

      /* Mouse repulsion */
      const dx = pos[i3]-this.#mw.x, dy = pos[i3+1]-this.#mw.y;
      const d2 = dx*dx + dy*dy;
      let mx = 0, my = 0;
      if (d2 < mr2 && d2 > 0.01) {
        const d = Math.sqrt(d2), f = (1 - d / 15) * 6;
        mx = dx/d*f; my = dy/d*f;
      }

      /* Scroll burst: radial outward explosion */
      const bx = sb * base[i3] * 0.05;
      const by = sb * base[i3+1] * 0.05;
      const bz = sb * base[i3+2] * 0.03;

      /* Integrate velocity with damping */
      vel[i3]   = vel[i3]*0.92   + (fx+mx+bx)*dt;
      vel[i3+1] = vel[i3+1]*0.92 + (fy+my+by)*dt;
      vel[i3+2] = vel[i3+2]*0.92 + (fz+bz)*dt;

      /* Final position: base target + velocity offset */
      pos[i3] = tx + vel[i3]; pos[i3+1] = ty + vel[i3+1]; pos[i3+2] = tz + vel[i3+2];
    }
    this.#geo.attributes.position.needsUpdate = true;

    /* Subtle rotation from mouse */
    this.#points.rotation.y = t * 0.015 + this.#mouse.x * this.sens * 0.3;
    this.#points.rotation.x = t * 0.01  + this.#mouse.y * this.sens * 0.15;
    this.#lines.rotation.copy(this.#points.rotation);

    /* Constellation lines (skip during entry & deep scroll) */
    if (this.#entry >= 1 && sp < 0.6) this.#updateLines(pos, n);
    else this.#lineGeo.setDrawRange(0, 0);

    this.#renderer.render(this.#scene, this.#camera);
  }

  /* ---- Events ---- */

  #bindEvents() {
    this.#onR  = this.#resize.bind(this);
    this.#onM  = this.#mmove.bind(this);
    this.#onS  = this.#scrl.bind(this);
    this.#onCL = this.#ctxLost.bind(this);
    this.#onCR = this.#ctxBack.bind(this);
    window.addEventListener('resize', this.#onR, { passive: true });
    window.addEventListener('mousemove', this.#onM, { passive: true });
    window.addEventListener('scroll', this.#onS, { passive: true });
    this.#canvas.addEventListener('webglcontextlost', this.#onCL);
    this.#canvas.addEventListener('webglcontextrestored', this.#onCR);
  }

  #resize() {
    if (this.#dead) return;
    const w = window.innerWidth, h = window.innerHeight;
    this.#camera.aspect = w / h; this.#camera.updateProjectionMatrix();
    this.#renderer.setSize(w, h);
    this.#mat.uniforms.uPixelRatio.value = Math.min(window.devicePixelRatio, 2);
    this.#cacheRect();
  }

  #mmove(e) {
    this.#mouse.tx = (e.clientX / window.innerWidth) * 2 - 1;
    this.#mouse.ty = (e.clientY / window.innerHeight) * 2 - 1;
  }

  #cacheRect() {
    const r = this.#canvas.getBoundingClientRect();
    this.#cachedRect.top = r.top + window.scrollY;
    this.#cachedRect.height = r.height;
  }

  #scrl() {
    const h = this.#cachedRect.height;
    if (h === 0) return;
    const top = this.#cachedRect.top - window.scrollY;
    this.#scroll.p = Math.min(Math.max(-top / h, 0), 1);
  }

  #ctxLost(e) {
    e.preventDefault();
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#canvas.style.display = 'none';
  }

  #ctxBack() {
    this.#canvas.style.display = '';
    if (!this.#reducedMotion) this.#loop();
  }

  destroy() {
    this.#dead = true;
    if (this.#raf) cancelAnimationFrame(this.#raf);
    window.removeEventListener('resize', this.#onR);
    window.removeEventListener('mousemove', this.#onM);
    window.removeEventListener('scroll', this.#onS);
    this.#canvas.removeEventListener('webglcontextlost', this.#onCL);
    this.#canvas.removeEventListener('webglcontextrestored', this.#onCR);
    if (this.#geo) this.#geo.dispose();
    if (this.#mat) this.#mat.dispose();
    if (this.#lineGeo) this.#lineGeo.dispose();
    if (this.#lineMat) this.#lineMat.dispose();
    if (this.#renderer) { this.#renderer.dispose(); this.#renderer.forceContextLoss(); }
  }
}

/* ======================================================================== */
/* Auto-initialise                                                          */
/* ======================================================================== */
function initHeroParticles() {
  const canvas = document.getElementById('HeroCanvas');
  if (!canvas) return;
  window.__heroParticles = new HeroParticles('HeroCanvas', {
    particleCount: parseInt(canvas.dataset.particleDensity, 10) || 200,
    particleColor: canvas.dataset.particleColor || '#f06306',
    mouseSensitivity: 0.3,
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initHeroParticles);
else initHeroParticles();
